/**
 * Credit-pack picker shown in the paywall and the Buy credits dialog. Fetches the
 * packs available on this environment (those with a configured Stripe price) and
 * starts a Stripe Checkout redirect on selection. Each card shows its price, the
 * list price struck through while the early-user discount is on (D1). A Custom
 * card and the no-packs state both open Contact us.
 */

import { useEffect, useState } from 'react';
import { Loader2, Check } from 'lucide-react';
import { billingAPI, type CreditPack } from '../../services/api';
import { Button } from '../ui/button';
import { EnterpriseContactDialog } from './EnterpriseContactDialog';
import { CREDIT_MODEL_NOTE } from '@/config/pricing';

const dollars = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: Number.isInteger(n) ? 0 : 2 });

const cardClass = (selected: boolean) =>
  [
    'relative rounded-xl border p-4 text-left transition-all',
    selected ? 'border-primary ring-2 ring-primary/30 bg-accent' : 'border-border hover:border-primary/50',
  ].join(' ');

interface CreditPackPickerProps {
  /** No longer rendered: an empty pack list shows the unavailable state and Contact us. */
  fallback?: React.ReactNode;
}

export const CreditPackPicker: React.FC<CreditPackPickerProps> = () => {
  const [loading, setLoading] = useState(true);
  const [packs, setPacks] = useState<CreditPack[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contactOpen, setContactOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    billingAPI
      .getPacks()
      .then((r) => {
        if (cancelled) return;
        const list = r.packs || [];
        setPacks(list);
        const def = list.find((p) => p.popular) || list[0];
        if (def) setSelected(def.id);
      })
      .catch(() => { if (!cancelled) setPacks([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  async function checkout() {
    if (!selected) return;
    setRedirecting(true);
    setError(null);
    try {
      const { url } = await billingAPI.startCheckout({ pack: selected });
      window.location.href = url;
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Could not start checkout. Please try again.';
      setError(typeof msg === 'string' ? msg : 'Could not start checkout.');
      setRedirecting(false);
    }
  }

  const contact = <EnterpriseContactDialog open={contactOpen} onOpenChange={setContactOpen} source="buy_credits" />;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (packs.length === 0) {
    return (
      <div className="space-y-3 py-2">
        <p className="text-sm text-muted-foreground">
          Credit packs are unavailable right now. Contact us and we'll add credits for you.
        </p>
        <Button variant="outline" onClick={() => setContactOpen(true)}>Contact us</Button>
        {contact}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        {packs.map((p) => {
          const isSel = selected === p.id;
          const discounted = p.discountPct > 0 && p.priceUsd < p.listUsd;
          return (
            <button key={p.id} type="button" data-testid="pack-card" onClick={() => setSelected(p.id)} className={cardClass(isSel)}>
              {p.popular && (
                <span className="absolute -top-2 right-3 rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold text-primary-foreground">
                  Popular
                </span>
              )}
              {isSel && (
                <span className="absolute top-3 right-3 text-primary">
                  <Check className="h-4 w-4" />
                </span>
              )}
              <div className="text-2xl font-bold">{p.credits.toLocaleString()}</div>
              <div className="text-xs text-muted-foreground">
                credits{p.videosApprox ? ` · about ${p.videosApprox} videos` : ''}
              </div>
              <div className="mt-3 flex items-baseline gap-2">
                {discounted && <s className="text-sm text-muted-foreground">{dollars(p.listUsd)}</s>}
                <span className="text-lg font-semibold">{dollars(p.priceUsd)}</span>
              </div>
              {discounted && (
                <div className="mt-1 text-[11px] font-medium text-accent-foreground">
                  Early-user price · {p.discountPct}% off
                </div>
              )}
            </button>
          );
        })}
        <button type="button" data-testid="pack-custom" onClick={() => setContactOpen(true)} className={cardClass(false)}>
          <div className="text-2xl font-bold">Custom</div>
          <div className="text-xs text-muted-foreground">Volume, teams, API</div>
          <div className="mt-3 text-sm font-semibold">Contact us</div>
        </button>
      </div>

      <p className="text-xs text-muted-foreground">
        {CREDIT_MODEL_NOTE}
      </p>

      {error && <p className="text-sm text-red-500">{error}</p>}

      <Button className="w-full" disabled={!selected || redirecting} onClick={checkout}>
        {redirecting ? (
          <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Redirecting to checkout…</>
        ) : (
          <>Buy {packs.find((p) => p.id === selected)?.credits.toLocaleString()} credits</>
        )}
      </Button>
      {contact}
    </div>
  );
};
